// src/components/AnnotationToolbar.tsx
/**
 * Annotation Toolbar Component (T-18)
 * 
 * Provides tool selection interface for the annotation layer.
 * Includes all annotation modes: selection, text, equation, pen, highlighter, 
 * eraser, shapes, lines, with color and thickness controls.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../types/redux';
import { 
  setActiveTool, 
  setActiveColor, 
  setThickness, 
  clearSelection,
  setToolArrowStart,
  setToolArrowEnd
} from '../store/slices/uiSlice';
import { 
  clearAnnotations, 
  clearStrokes, 
  deleteSelectedAnnotations,
  updateShape,
  updateText,
  updateStroke
} from '../store/slices/annotationsSlice';

import ConfirmationDialog from './ConfirmationDialog';
import type { ToolType } from '../types/redux';

// Color swatch component with preview
interface ColorSwatchProps {
  color: string;
  label: string;
  isSelected: boolean;
  isDark: boolean;
  onClick: () => void;
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({ color, label, isSelected, isDark, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 w-full px-2 py-1.5 text-left transition-colors ${
      isSelected 
        ? isDark ? 'bg-blue-600' : 'bg-blue-100' 
        : isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
    }`}
  >
    <div 
      className={`w-5 h-5 rounded border-2 flex-shrink-0 ${
        color === '#FFFFFF' 
          ? isDark ? 'border-gray-500' : 'border-gray-300' 
          : 'border-transparent'
      }`}
      style={{ backgroundColor: color }}
    />
    <span className={`text-xs ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
      {label}
    </span>
    {isSelected && (
      <span className={`ml-auto text-xs ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>✓</span>
    )}
  </button>
);

// Custom color picker dropdown component
interface ColorPickerDropdownProps {
  currentColor: string;
  onColorSelect: (color: string) => void;
  isDark: boolean;
}

const ColorPickerDropdown: React.FC<ColorPickerDropdownProps> = ({ currentColor, onColorSelect, isDark }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Color palette with labels - includes white for dark mode
  const colors = [
    { color: '#000000', label: 'Black' },
    { color: '#FFFFFF', label: 'White' },
    { color: '#3B82F6', label: 'Blue' },
    { color: '#EF4444', label: 'Red' },
    { color: '#10B981', label: 'Green' },
    { color: '#F59E0B', label: 'Amber' },
    { color: '#8B5CF6', label: 'Purple' },
    { color: '#F97316', label: 'Orange' },
    { color: '#6B7280', label: 'Gray' },
    { color: '#EC4899', label: 'Pink' },
    { color: '#14B8A6', label: 'Teal' },
    { color: '#84CC16', label: 'Lime' },
  ];

  const currentColorInfo = colors.find(c => c.color === currentColor) || { color: currentColor, label: currentColor };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-2 py-1 rounded border transition-colors ${
          isDark 
            ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-200' 
            : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
        }`}
        style={{ height: '36px', width: '80px' }}
        title="Select color"
      >
        <div 
          className={`w-5 h-5 rounded border ${
            currentColor === '#FFFFFF' 
              ? isDark ? 'border-gray-500' : 'border-gray-300' 
              : 'border-transparent'
          }`}
          style={{ backgroundColor: currentColor }}
        />
        <span className="text-xs truncate flex-1 text-left">{currentColorInfo.label}</span>
        <svg 
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div 
          className={`absolute top-full left-0 mt-1 rounded-lg shadow-lg border z-50 overflow-hidden ${
            isDark 
              ? 'bg-gray-800 border-gray-600' 
              : 'bg-white border-gray-200'
          }`}
          style={{ minWidth: '140px' }}
        >
          <div className="py-1 max-h-64 overflow-y-auto">
            {colors.map(({ color, label }) => (
              <ColorSwatch
                key={color}
                color={color}
                label={label}
                isSelected={currentColor === color}
                isDark={isDark}
                onClick={() => {
                  onColorSelect(color);
                  setIsOpen(false);
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface AnnotationToolbarProps {
  /** Whether to show the toolbar */
  visible?: boolean;
  /** Orientation of the toolbar */
  orientation?: 'horizontal' | 'vertical';
  /** Additional CSS classes */
  className?: string;
}

// Tool configuration with icons and labels
const ANNOTATION_TOOLS: Array<{
  id: ToolType;
  label: string;
  icon: string;
  category: 'selection' | 'drawing' | 'shapes' | 'utility';
  description: string;
}> = [
  // Selection and Navigation
  { id: 'select', label: 'Select', icon: '🖱️', category: 'selection', description: 'Select and move annotations' },
  
  // Drawing Tools
  { id: 'pen', label: 'Pen', icon: '🖊️', category: 'drawing', description: 'Draw freehand lines' },
  { id: 'highlighter', label: 'Highlighter', icon: '🖍️', category: 'drawing', description: 'Draw transparent highlights' },
  
  // Text and Math
  { id: 'text', label: 'Text', icon: '📝', category: 'drawing', description: 'Add text annotations' },
  { id: 'equation', label: 'Equation', icon: '∑', category: 'drawing', description: 'Add mathematical equations' },
  
  // Shapes
  { id: 'line', label: 'Line', icon: '📏', category: 'shapes', description: 'Draw straight lines with optional arrows' },
  { id: 'rectangle', label: 'Rectangle', icon: '⬜', category: 'shapes', description: 'Draw outlined rectangles' },
  { id: 'rectangle-filled', label: 'Filled Rectangle', icon: '⬛', category: 'shapes', description: 'Draw filled rectangles' },
  { id: 'circle', label: 'Circle', icon: '⭕', category: 'shapes', description: 'Draw outlined circles' },
  
  // Utility
  { id: 'eraser', label: 'Eraser', icon: '🧽', category: 'utility', description: 'Delete annotations' },
];

// Color palette for annotations - includes white for dark mode usage
const COLOR_PALETTE = [
  '#000000', // Black
  '#FFFFFF', // White
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#F97316', // Orange
  '#6B7280', // Gray
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#84CC16', // Lime
];

// Thickness options
const THICKNESS_OPTIONS = [1, 2, 3, 4, 6, 8, 12, 16];

export default function AnnotationToolbar({
  visible = true,
  orientation = 'horizontal',
  className = '',
}: AnnotationToolbarProps) {
  const dispatch = useDispatch();
  const ui = useSelector((state: RootState) => state.ui);
  const annotations = useSelector((state: RootState) => state.annotations);
  const preferences = useSelector((state: RootState) => state.preferences);
  const isDark = preferences.theme === 'dark';

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    onConfirm: () => void;
    isDestructive: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: () => {},
    isDestructive: false,
  });

  // Handle tool selection
  const handleToolSelect = (tool: ToolType) => {
    dispatch(setActiveTool(tool));
    dispatch(clearSelection());
  };

  // Handle color selection
  const handleColorSelect = (color: string) => {
    // Check if we have selected annotations
    const selectedShapes = annotations.shapes.filter(shape => shape.selected);
    const selectedTexts = annotations.texts.filter(text => text.selected);
    const selectedStrokes = annotations.strokes.filter(stroke => stroke.selected);
    
    if (selectedShapes.length > 0 || selectedTexts.length > 0 || selectedStrokes.length > 0) {
      // Update all selected annotations
      selectedShapes.forEach(shape => {
        dispatch(updateShape({
          id: shape.id,
          updates: {
            style: {
              ...shape.style,
              color,
            }
          }
        }));
      });
      
      selectedTexts.forEach(text => {
        dispatch(updateText({
          id: text.id,
          updates: {
            style: {
              ...text.style,
              color,
            }
          }
        }));
      });
      
      selectedStrokes.forEach(stroke => {
        dispatch(updateStroke({
          id: stroke.id,
          updates: {
            style: {
              ...stroke.style,
              color,
            }
          }
        }));
      });
    } else {
      // Update tool settings for new annotations
      dispatch(setActiveColor(color));
    }
    // Note: Color picker closing is now handled by the custom dropdown component
  };

  // Handle thickness change
  const handleThicknessChange = (thickness: number) => {
    // Check if we have selected shape annotations (shapes and strokes can have thickness)
    const selectedShapes = annotations.shapes.filter(shape => shape.selected);
    const selectedStrokes = annotations.strokes.filter(stroke => stroke.selected);
    
    if (selectedShapes.length > 0 || selectedStrokes.length > 0) {
      // Update all selected shape annotations
      selectedShapes.forEach(shape => {
        dispatch(updateShape({
          id: shape.id,
          updates: {
            style: {
              ...shape.style,
              thickness,
            }
          }
        }));
      });
      
      selectedStrokes.forEach(stroke => {
        dispatch(updateStroke({
          id: stroke.id,
          updates: {
            style: {
              ...stroke.style,
              thickness,
            }
          }
        }));
      });
    } else {
      // Update tool settings for new annotations
      dispatch(setThickness(thickness));
    }
  };

  // Handle arrow start toggle
  const handleArrowStartToggle = () => {
    const currentSettings = ui.toolSettings.line;
    dispatch(setToolArrowStart({ 
      tool: 'line', 
      arrowStart: !currentSettings.arrowStart 
    }));
  };

  // Handle arrow end toggle
  const handleArrowEndToggle = () => {
    const currentSettings = ui.toolSettings.line;
    dispatch(setToolArrowEnd({ 
      tool: 'line', 
      arrowEnd: !currentSettings.arrowEnd 
    }));
  };

  // Handle arrow option change
  const handleArrowOptionChange = (option: string) => {
    // Check if we have selected line annotations
    const selectedLines = annotations.shapes.filter(shape => 
      shape.type === 'line' && shape.selected
    );
    
    let arrowStart = false;
    let arrowEnd = false;
    
    switch (option) {
      case 'none':
        arrowStart = false;
        arrowEnd = false;
        break;
      case 'start':
        arrowStart = true;
        arrowEnd = false;
        break;
      case 'end':
        arrowStart = false;
        arrowEnd = true;
        break;
      case 'both':
        arrowStart = true;
        arrowEnd = true;
        break;
    }
    
    if (selectedLines.length > 0) {
      // Update all selected line annotations
      selectedLines.forEach(line => {
        dispatch(updateShape({
          id: line.id,
          updates: {
            style: {
              ...line.style,
              arrowStart,
              arrowEnd,
            }
          }
        }));
      });
    } else {
      // Update tool settings for new lines
      dispatch(setToolArrowStart({ tool: 'line', arrowStart }));
      dispatch(setToolArrowEnd({ tool: 'line', arrowEnd }));
    }
  };

  // Get current arrow option for dropdown
  const getCurrentArrowOption = () => {
    // Check if we have selected line annotations
    const selectedLines = annotations.shapes.filter(shape => 
      shape.type === 'line' && shape.selected
    );
    
    if (selectedLines.length > 0) {
      // Use the first selected line's arrow settings
      const selectedLine = selectedLines[0];
      const arrowStart = selectedLine.style.arrowStart || false;
      const arrowEnd = selectedLine.style.arrowEnd || false;
      
      if (arrowStart && arrowEnd) return 'both';
      if (arrowStart) return 'start';
      if (arrowEnd) return 'end';
      return 'none';
    }
    
    // Fallback to tool settings for new lines
    const currentSettings = ui.toolSettings.line;
    const arrowStart = currentSettings.arrowStart || false;
    const arrowEnd = currentSettings.arrowEnd || false;
    
    if (arrowStart && arrowEnd) return 'both';
    if (arrowStart) return 'start';
    if (arrowEnd) return 'end';
    return 'none';
  };

  // Check if arrow controls should be visible
  const shouldShowArrowControls = () => {
    // Show when line tool is active OR when line annotations are selected
    const hasSelectedLines = annotations.shapes.some(shape => 
      shape.type === 'line' && shape.selected
    );
    return ui.activeTool === 'line' || hasSelectedLines;
  };

  // Get current color for color picker
  const getCurrentColor = () => {
    // Check if we have selected annotations
    const selectedShapes = annotations.shapes.filter(shape => shape.selected);
    const selectedTexts = annotations.texts.filter(text => text.selected);
    const selectedStrokes = annotations.strokes.filter(stroke => stroke.selected);
    
    if (selectedShapes.length > 0) {
      return selectedShapes[0].style.color;
    }
    if (selectedTexts.length > 0) {
      return selectedTexts[0].style.color;
    }
    if (selectedStrokes.length > 0) {
      return selectedStrokes[0].style.color;
    }
    
    // Fallback to tool color
    return ui.colorPicker.activeColor;
  };

  // Get current thickness for thickness picker
  const getCurrentThickness = () => {
    // Check if we have selected shape annotations
    const selectedShapes = annotations.shapes.filter(shape => shape.selected);
    const selectedStrokes = annotations.strokes.filter(stroke => stroke.selected);
    
    if (selectedShapes.length > 0) {
      return selectedShapes[0].style.thickness;
    }
    if (selectedStrokes.length > 0) {
      return selectedStrokes[0].style.thickness;
    }
    
    // Fallback to tool thickness
    return ui.thickness;
  };

  // Handle clear operations
  const handleClearAll = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Clear All Annotations',
      message: 'This will permanently delete all annotations (shapes, text, equations, and strokes). This action cannot be undone.',
      confirmText: 'Clear All',
      onConfirm: () => {
        dispatch(clearAnnotations());
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
      isDestructive: true,
    });
  };

  const handleClearStrokes = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Clear All Strokes',
      message: 'This will permanently delete all pen and highlighter strokes. This action cannot be undone.',
      confirmText: 'Clear Strokes',
      onConfirm: () => {
        dispatch(clearStrokes());
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
      isDestructive: true,
    });
  };

  const handleDeleteSelected = () => {
    const selectedCount = 
      annotations.shapes.filter(s => s.selected).length +
      annotations.texts.filter(t => t.selected).length +
      annotations.strokes.filter(s => s.selected).length;
    
    if (selectedCount > 0) {
      dispatch(deleteSelectedAnnotations());
    }
  };

  // Handle dialog cancel
  const handleDialogCancel = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  if (!visible) return null;

  const isHorizontal = orientation === 'horizontal';

    return (
    <div className={`annotation-toolbar ${className}`}>
      <div 
        className="flex flex-wrap items-center gap-2"
        style={{ 
          userSelect: 'none', 
          marginTop: '-8px', 
          marginBottom: '-8px',
          alignItems: 'flex-start'
        }}
      >
        {/* Tool Buttons Group */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {ANNOTATION_TOOLS.map((tool) => (
            <button
              key={tool.id}
              onClick={() => handleToolSelect(tool.id)}
              className={`
                tool-button p-0 rounded transition-colors flex items-center justify-center border-0
                ${ui.activeTool === tool.id
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }
              `}
              style={{ 
                width: '36px', 
                height: '36px', 
                fontSize: '16px',
                lineHeight: '1.2',
                minWidth: '36px',
                minHeight: '36px',
                paddingBottom: '4px',
                paddingTop: '4px'
              }}
              title={tool.description}
            >
              {tool.icon}
            </button>
          ))}
        </div>

        {/* Tool Settings Group */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Color Picker */}
          <div className="color-section">
            <ColorPickerDropdown
              currentColor={getCurrentColor()}
              onColorSelect={handleColorSelect}
              isDark={isDark}
            />
          </div>

          {/* Thickness Selector */}
          <div className="thickness-section">
            <select
              value={getCurrentThickness()}
              onChange={(e) => handleThicknessChange(Number(e.target.value))}
              className={`thickness-select px-2 py-0.5 text-xs border rounded ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-gray-100' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              title="Line thickness"
              style={{
                lineHeight: '1',
                height: '36px'
              }}
            >
              {THICKNESS_OPTIONS.map((thickness) => (
                <option key={thickness} value={thickness}>
                  {thickness}px
                </option>
              ))}
            </select>
          </div>

          {/* Arrow Controls - Only show when line tool is active or lines are selected */}
          {shouldShowArrowControls() && (
            <div className="arrow-controls" style={{ width: '95px', minWidth: '95px' }}>
              <select
                value={getCurrentArrowOption()}
                onChange={(e) => handleArrowOptionChange(e.target.value)}
                className={`arrow-select px-2 py-0.5 text-xs border rounded ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-gray-100' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                title="Arrow options"
                style={{
                  lineHeight: '1',
                  height: '36px',
                  width: '100%'
                }}
              >
                <option value="none">None</option>
                <option value="start">Start</option>
                <option value="end">End</option>
                <option value="both">Both</option>
              </select>
            </div>
          )}
        </div>

        {/* Action Buttons Group */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleDeleteSelected}
            disabled={ui.selectedObjects.length === 0}
            className="px-3 py-0.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            title="Delete selected annotations"
            style={{ height: '36px', lineHeight: '1.2', paddingBottom: '4px', paddingTop: '4px' }}
          >
            🗑️
          </button>
          
          <button
            onClick={handleClearStrokes}
            className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
            title="Clear all pen and highlighter strokes"
            style={{ height: '36px', width: '36px', minWidth: '36px', lineHeight: '1.2', paddingBottom: '4px', paddingTop: '4px' }}
          >
            🧽
          </button>
          
          <button
            onClick={handleClearAll}
            className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            title="Clear all annotations"
            style={{ height: '36px', width: '36px', minWidth: '36px', lineHeight: '1.2', paddingBottom: '4px', paddingTop: '4px' }}
          >
            🚫
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        isDestructive={confirmDialog.isDestructive}
        onConfirm={confirmDialog.onConfirm}
        onCancel={handleDialogCancel}
      />
    </div>
  );
}